import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ConversationDeleteResponse,
  ConversationDetailResponse,
  ConversationsListResponse,
  QaChunk,
  type QaCitationSourceType,
  type QaRequest,
} from "@oracus/shared/api/qa";

import { apiClient } from "./client.ts";

export interface UseQaStreamState {
  chunks: QaChunk[];
  isStreaming: boolean;
  error: string | null;
  /** Conversation id this stream is appending to (set on first `conversation` chunk). */
  conversationId: string | null;
}

export function useQaStream() {
  const [state, setState] = useState<UseQaStreamState>({
    chunks: [],
    isStreaming: false,
    error: null,
    conversationId: null,
  });
  const qc = useQueryClient();

  const reset = useCallback(() => {
    setState({
      chunks: [],
      isStreaming: false,
      error: null,
      conversationId: null,
    });
  }, []);

  const ask = useCallback(
    async (input: QaRequest) => {
      setState({
        chunks: [],
        isStreaming: true,
        error: null,
        conversationId: input.conversationId ?? null,
      });
      try {
        const res = await fetch("/api/qa", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        });
        if (!res.ok || !res.body) {
          throw new Error(`${res.status} ${res.statusText}`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const events = buffer.split("\n\n");
          buffer = events.pop() ?? "";

          for (const event of events) {
            if (!event.startsWith("data: ")) continue;
            const json = event.slice(6).trim();
            if (!json) continue;
            let parsed: unknown;
            try {
              parsed = JSON.parse(json);
            } catch {
              continue;
            }
            const result = QaChunk.safeParse(parsed);
            if (!result.success) continue;
            const chunk = result.data;
            setState((prev) => ({
              ...prev,
              chunks: [...prev.chunks, chunk],
              ...(chunk.type === "conversation"
                ? { conversationId: chunk.conversationId }
                : {}),
            }));
            if (chunk.type === "done") {
              // Invalidate conversations list + the active conversation's
              // history so the sidebar and any open detail view refresh.
              qc.invalidateQueries({ queryKey: ["conversations"] });
              return;
            }
            if (chunk.type === "error") {
              const message = chunk.message;
              setState((prev) => ({ ...prev, error: message }));
              return;
            }
          }
        }
      } catch (err) {
        setState((prev) => ({
          ...prev,
          error: err instanceof Error ? err.message : String(err),
        }));
      } finally {
        setState((prev) => ({ ...prev, isStreaming: false }));
      }
    },
    [qc],
  );

  return { ...state, ask, reset };
}

export function useConversations() {
  return useQuery({
    queryKey: ["conversations"],
    queryFn: () =>
      apiClient.get("/api/conversations", ConversationsListResponse),
  });
}

export function useConversation(id: string | null) {
  return useQuery({
    queryKey: ["conversations", id],
    queryFn: () =>
      apiClient.get(`/api/conversations/${id}`, ConversationDetailResponse),
    enabled: id !== null,
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(
        `/api/conversations/${id}`,
        ConversationDeleteResponse,
      ),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["conversations"] });
      qc.removeQueries({ queryKey: ["conversations", id] });
    },
  });
}

export type CitationSource = QaCitationSourceType;
